import { authFetch, authFetchRaw } from './client';

export const listProjects = () => authFetch('/api/projects');
// `description` is the composer's prompt, verbatim. `POST /{id}/summarize`
// rewrites it (and the name) as the short forms the dashboard rail shows.
export const createProject = (name, description = '') =>
  authFetch('/api/projects', { method: 'POST', body: JSON.stringify({ name, description }) });
// Best-effort: rewrites `name` + `description` from the stored prompt via the
// org's LLM key. Callers do NOT await it — a project must not wait on a round
// trip, and any failure leaves the locally-derived name in place.
export const summarizeProject = (id) =>
  authFetch(`/api/projects/${id}/summarize`, { method: 'POST' });
export const renameProject = (id, name) =>
  authFetch(`/api/projects/${id}`, { method: 'PATCH', body: JSON.stringify({ name }) });
// Star / unstar. Same PATCH as renameProject — the route is a partial patch, so
// an omitted field is left alone rather than cleared.
export const setProjectPinned = (id, pinned) =>
  authFetch(`/api/projects/${id}`, { method: 'PATCH', body: JSON.stringify({ pinned }) });
// The agent's permission mode (`auto` | `manual` | `plan`) — the user's sticky
// per-project choice. Normally set through `setAgentMode` in `api/agentRun.js`,
// which writes this row AND patches any live run in one request; this wrapper
// exists for a caller with no run to consider.
export const setProjectAgentMode = (id, agentMode) =>
  authFetch(`/api/projects/${id}`, { method: 'PATCH', body: JSON.stringify({ agentMode }) });
// Stamp the caller's "I have seen this" watermark, which is what clears the
// dashboard's "Needs attention" badge. Fire-and-forget at the call site — a
// failure here must never block opening a project. `owner` for a shared project
// (read access is enough; the write lands on the caller's own partition).
export const markProjectSeen = (id, owner) =>
  authFetch(`/api/projects/${id}/seen${owner ? `?owner=${encodeURIComponent(owner)}` : ''}`, {
    method: 'POST',
  });
export const deleteProject = (id) => authFetch(`/api/projects/${id}`, { method: 'DELETE' });
// HS-84: erase one tile's backend memory (per-node DynamoDB records + S3 folder).
export const deleteProjectNode = (id, nodeId) =>
  authFetch(`/api/projects/${id}/nodes/${nodeId}`, { method: 'DELETE' });
// Copy a project (own or, with `owner`, one shared into the caller's org) into a
// new caller-owned project. Default name (when omitted) is "<original> copy".
export const copyProject = (id, { name, owner } = {}) =>
  authFetch(`/api/projects/${id}/copy${owner ? `?owner=${encodeURIComponent(owner)}` : ''}`, {
    method: 'POST',
    body: JSON.stringify({ name: name ?? null }),
  });

// Full-fidelity backup. Returns a binary .zip Blob (NOT JSON), so it can't use
// authFetch — fetch with the token and read res.blob(). `owner` exports a project
// shared into the caller's org (read access required).
export async function exportProject(id, owner) {
  const qs = owner ? `?owner=${encodeURIComponent(owner)}` : '';
  const res = await authFetchRaw(`/api/projects/${id}/export${qs}`);
  return res.blob();
}

// Restore a backup .zip as a new caller-owned project. Sent as multipart/form-data
// (no Content-Type header — the browser sets the multipart boundary).
export async function importProject(file) {
  const form = new FormData();
  form.append('file', file);
  const res = await authFetchRaw('/api/projects/import', { method: 'POST', body: form });
  return res.json();
}
